import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { batch, useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { useDropzone } from 'react-dropzone';
import { useTheme } from 'emotion-theming';
import Text from '../../../shared/components/Text/Text';
import TBToggleSwitch from '../../../shared/components/TBToggleSwitch/TBToggleSwitch';
import {
  toggleAnalyticsStrikethrough,
  updateCalendarSettings,
  setBuildAppearance,
  setIsTvMode,
  setIsPrimaryAnimatedSideBarOpen,
} from '../ducks/calendarActions';
import ModalCtaButton from '../components/presentational/ModalCtaButton';
import AppearancePicker from '../components/presentational/AppearancePicker';
import { fetchUserData } from '../../login/ducks/loginActions';
import {
  readStoredDefaultView,
  writeStoredDefaultView,
  readStoredProgramDefaultView,
  writeStoredProgramDefaultView,
} from '../utils/buildDefaultView';

const ModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const TabsContainer = styled('div')`
  display: flex;
  gap: 32px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: 24px;
`;

const Tab = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${(props) => (props.isActive ? props.theme.colors.text : props.theme.colors.textSecondary)};
  padding-bottom: 8px;
  border-bottom: 2px solid ${(props) => (props.isActive ? props.theme.colors.text : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const ContentSection = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const SelectContainer = styled('div')`
  min-width: ${(props) => (props.minWidth ? `${props.minWidth}px` : 'auto')};
  .react-select__control {
    background-color: ${({ theme }) => theme.colors.surfaceInput};
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: 4px;
    min-height: 40px;
    box-shadow: none;

    &:hover {
      border-color: ${({ theme }) => theme.colors.text};
    }
  }

  .react-select__value-container {
    padding: 2px 12px;
  }

  .react-select__input input { background-color: transparent !important; }
  .react-select__single-value {
    color: ${({ theme }) => theme.colors.text};
    font-family: 'Nunito Sans';
    font-size: 14px;
  }

  .react-select__menu {
    background-color: ${({ theme }) => theme.colors.surfaceElevated};
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  .react-select__option {
    font-family: 'Nunito Sans';
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
    
    &:active {
      background-color: ${({ theme }) => theme.colors.surfaceSecondary};
    }
  }

  .react-select__option--is-selected {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
    color: ${({ theme }) => theme.colors.text};
  }

  .react-select__option--is-focused {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const ImageUploadArea = styled('div')`
  width: 100%;
  height: 120px;
  border: 1px dashed ${(props) => (props.isDragActive ? props.theme.colors.text : props.theme.colors.borderLight)};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.isDragActive ? props.theme.colors.surfaceHover : props.theme.colors.surface)};
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const ImagePreviewContainer = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
`;

const ImagePreview = styled('img')`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const RemoveButton = styled('div')`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.overlayMedium};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.overlayStrong};
  }
`;

const ImageUploadText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 8px;
`;

const FileTypeText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorText = styled(Text)`
  color: #E53935;
  font-size: 12px;
  margin-top: 8px;
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const ShortcutSection = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ShortcutCategory = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CategoryTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const ShortcutRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surfaceSecondary};
`;

const ShortcutAction = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const ShortcutKey = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const RowContainer = styled('div')`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;

const ToggleRow = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const ToggleCopy = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToggleHeading = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const ToggleDescription = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/** The same heading as a <label>, so a visible heading can name a form control
  * via htmlFor. react-select renders a dummy input when isSearchable is false
  * and drops aria-label on that path, so inputId + a real label is the only way
  * to give these selects an accessible name. */
const ToggleHeadingLabel = styled(ToggleHeading.withComponent('label'))`
  margin-bottom: 0;
`;

const PhoneOnly = styled('div')`
  display: none;
  @media screen and (max-width: 570px) {
    display: contents;
  }
`;

const HalfWidthFormGroup = styled(FormGroup)`
  flex: 1;
`;

/** Ties the default-view heading to react-select's inner input. Only one of the
  * two default-view rows renders at a time, so they share it. */
const DEFAULT_ENTRY_VIEW_INPUT_ID = 'build-default-entry-view';

/** Values match Redux's viewType; labels match the header's view menu. */
const SCHEDULED_ENTRY_VIEW_OPTIONS = [
  { value: 'dayView', label: 'Day' },
  { value: 'weekView', label: 'Week' },
  { value: 'monthView', label: 'Month' },
];
/** The Program Library has no month view. */
const PROGRAM_ENTRY_VIEW_OPTIONS = [
  { value: 'dayView', label: 'Day' },
  { value: 'weekView', label: 'Week' },
];

const SettingsModal = ({ closeModal }) => {
  const { buildUndo } = useFlags();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('General');
  const tabs = ['General', 'Keyboard Shortcuts'];
  const parentCalendars = useSelector((state) => state.calendar.data.parentCalendars);
  const isAnalyticsDisabled = useSelector((state) => state.calendar.ui.isAnalyticsDisabled);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [scheduledEntryView, setScheduledEntryView] = useState(() => readStoredDefaultView());
  const [programEntryView, setProgramEntryView] = useState(() => readStoredProgramDefaultView());
  const [selectedDay, setSelectedDay] = useState({ value: 'monday', label: 'Monday' });
  const [headerImage, setHeaderImage] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const appearance = useSelector((state) => state.calendar.ui.appearance);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const programType = useSelector((state) => state.calendar.data.programType);
  // A default calendar only applies to the calendar view, not the Program Library.
  const isProgramLibrary = programType === 'free-form-program';
  // The Program Library has no month view for the arrow keys to step through.
  const navUnits = isProgramLibrary ? 'Day / Week' : 'Day / Week / Month';

  const dayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  const hasAutofilledDefaultCalendar = useRef(false);
  const userTouchedPickerRef = useRef(false);
  const { colors } = useTheme();

  const handleCalendarChange = (option) => {
    userTouchedPickerRef.current = true;
    setSelectedCalendar(option);
  };

  const handleAppearanceChange = (value) => {
    dispatch(setBuildAppearance(value));
  };

  const handleToggleTvMode = () => {
    batch(() => {
      dispatch(setIsTvMode(true));
      dispatch(setIsPrimaryAnimatedSideBarOpen(false));
    });
    closeModal();
  };

  /** Scheduled workouts and programs each keep their own default view; only the
   * one for the mode the coach is in is shown. Both are localStorage-only, like
   * appearance, so they apply on change rather than on Save. Neither dispatches
   * setViewType: this is the view a mode *opens* on, so moving the coach out of
   * the view they're working in would be a surprise. */
  const entryView = isProgramLibrary
    ? {
      heading: 'Default Program View',
      description: 'The view programs open on.',
      options: PROGRAM_ENTRY_VIEW_OPTIONS,
      value: programEntryView,
      onChange: (option) => setProgramEntryView(writeStoredProgramDefaultView(option.value)),
    }
    : {
      heading: 'Default Scheduled Workout View',
      description: 'The view scheduled workouts open on.',
      options: SCHEDULED_ENTRY_VIEW_OPTIONS,
      value: scheduledEntryView,
      onChange: (option) => setScheduledEntryView(writeStoredDefaultView(option.value)),
    };

  useEffect(() => {
    if (userTouchedPickerRef.current) return;
    if (hasAutofilledDefaultCalendar.current) return;
    if (!currentUser?.defaultCalendar || !parentCalendars?.length) return;

    const { defaultCalendar, defaultCalendarParentId } = currentUser;
    // The API can send these ids as strings; compare as numbers so a match isn't missed.
    const defaultId = Number(defaultCalendar);
    const defaultParentId = defaultCalendarParentId ? Number(defaultCalendarParentId) : null;
    let match = null;

    parentCalendars.forEach((parent) => {
      if (defaultParentId) {
        if (Number(parent.id) !== defaultParentId) return;
        parent.childCalendars?.forEach((child) => {
          if (Number(child.id) === defaultId) {
            match = {
              value: child.id,
              label: child.name,
              isChild: true,
              parentId: parent.id,
            };
          }
        });
      } else if (Number(parent.id) === defaultId) {
        match = {
          value: parent.id,
          label: parent.name,
          isParent: true,
        };
      }
    });

    if (match) {
      setSelectedCalendar(match);
      hasAutofilledDefaultCalendar.current = true;
    }
  }, [currentUser?.defaultCalendar, currentUser?.defaultCalendarParentId, parentCalendars]);

  const getCalendarOptions = () => {
    const options = [
      { value: 0, label: 'Select a default calendar' },
    ];

    parentCalendars?.forEach((parent) => {
      // Add parent calendar
      options.push({
        value: parent.id,
        label: parent.name,
        isParent: true,
      });

      // Add child calendars if they exist
      if (parent.childCalendars?.length > 0) {
        parent.childCalendars.forEach((child) => {
          options.push({
            value: child.id,
            label: child.name,
            isChild: true,
            parentId: parent.id,
          });
        });
      }
    });

    return options;
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.isChild ? '32px' : '16px',
      backgroundColor: state.isSelected ? colors.surfaceSecondary : colors.surface,
      color: colors.text,
      '&:hover': {
        backgroundColor: colors.surfaceSecondary,
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: colors.surfaceElevated,
      border: `1px solid ${colors.borderLight}`,
      boxShadow: 'none',
    }),
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setUploadError('Please upload a valid .jpg or .jpeg file');
      return;
    }

    const file = acceptedFiles[0];
    setUploadError(null);
    setHeaderImage({
      file,
      preview: URL.createObjectURL(file),
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setHeaderImage(null);
    setUploadError(null);
  };

  const handleSave = () => {
    if (selectedCalendar && !isProgramLibrary) {
      dispatch(updateCalendarSettings(
        currentUser.accountCode,
        currentUser.id,
        { defaultCalendar: selectedCalendar.value },
        {
          onSuccess: () => {
            // Nothing else updates currentUser after this save; refresh it so reopening
            // Settings shows the new default.
            dispatch(fetchUserData());
            closeModal();
          },
          onError: (error) => {
            console.error('Failed to update calendar settings:', error);
          },
        },
      ));
    } else {
      closeModal();
    }
  };

  return (
    <ModalContent>
      <Header>
        <Title>Settings</Title>
      </Header>

      <TabsContainer>
        {tabs.map((tab) => (
          <Tab
            key={tab}
            isActive={activeTab === tab}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </Tab>
        ))}
      </TabsContainer>

      {activeTab === 'General' && (
        <ContentSection>
          {!isProgramLibrary && (
            <FormGroup>
              <Label>Default Calendar</Label>
              <SelectContainer>
                <Select
                  options={getCalendarOptions()}
                  value={selectedCalendar}
                  onChange={handleCalendarChange}
                  styles={customStyles}
                  className='react-select'
                  classNamePrefix='react-select'
                  placeholder='Select a calendar'
                  isSearchable
                  autoFocus
                />
              </SelectContainer>
            </FormGroup>
          )}

          <FormGroup>
            <ToggleCopy>
              <ToggleHeadingLabel htmlFor={DEFAULT_ENTRY_VIEW_INPUT_ID}>
                {entryView.heading}
              </ToggleHeadingLabel>
              <ToggleDescription>{entryView.description}</ToggleDescription>
            </ToggleCopy>
            <SelectContainer>
              <Select
                inputId={DEFAULT_ENTRY_VIEW_INPUT_ID}
                options={entryView.options}
                value={entryView.options.find((option) => option.value === entryView.value)}
                onChange={entryView.onChange}
                styles={customStyles}
                className='react-select'
                classNamePrefix='react-select'
                isSearchable={false}
              />
            </SelectContainer>
          </FormGroup>

          {/* Stats bar visibility is a local, redux-persisted UI preference rather than a
              server setting, so it applies on change instead of waiting for Save. State is
              stored inverted as isAnalyticsDisabled. */}
          <FormGroup>
            <ToggleRow>
              <ToggleCopy>
                <ToggleHeading>Show Stats Bar</ToggleHeading>
                <ToggleDescription>
                  Sets / Reps / Volume totals on the calendar. Applies immediately.
                </ToggleDescription>
              </ToggleCopy>
              <TBToggleSwitch
                ariaLabel='Show stats bar'
                checked={!isAnalyticsDisabled}
                onChange={() => dispatch(toggleAnalyticsStrikethrough())}
              />
            </ToggleRow>
          </FormGroup>

          <PhoneOnly>
            <FormGroup>
              <ToggleRow>
                <ToggleCopy>
                  <ToggleHeading>TV Mode</ToggleHeading>
                  <ToggleDescription>
                    Show the calendar full-screen for display on a TV.
                  </ToggleDescription>
                </ToggleCopy>
                <TBToggleSwitch
                  ariaLabel='TV mode'
                  checked={isTvMode}
                  onChange={handleToggleTvMode}
                />
              </ToggleRow>
            </FormGroup>
          </PhoneOnly>

          {/* <RowContainer>
            <HalfWidthFormGroup>
              <Label>Start Week On</Label>
              <SelectContainer>
                <Select
                  options={dayOptions}
                  value={selectedDay}
                  onChange={setSelectedDay}
                  styles={customStyles}
                  className='react-select'
                  classNamePrefix='react-select'
                  isSearchable={false}
                />
              </SelectContainer>
            </HalfWidthFormGroup>
          </RowContainer>

          <FormGroup>
            <Label>Header Image</Label>
            <Text style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px' }}>
              Recommended size: 1060 x 420
            </Text>
            <ImageUploadArea {...getRootProps()} isDragActive={isDragActive}>
              <input {...getInputProps()} />
              {headerImage ? (
                <ImagePreviewContainer>
                  <ImagePreview src={headerImage.preview} alt='Header preview' />
                  <RemoveButton onClick={handleRemoveImage}>
                    <IcomoonReact
                      iconSet={iconSet}
                      icon='close'
                      size={16}
                      color={colors.textMuted}
                    />
                  </RemoveButton>
                </ImagePreviewContainer>
              ) : (
                <>
                  <ImageUploadText>Click or drag image here.</ImageUploadText>
                  <FileTypeText>Accepted file types: .jpg and .jpeg</FileTypeText>
                </>
              )}
            </ImageUploadArea>
            {uploadError && <ErrorText>{uploadError}</ErrorText>}
          </FormGroup> */}

          <FormGroup>
            <ToggleCopy>
              <ToggleHeading>Appearance</ToggleHeading>
              <ToggleDescription>
                Choose how Build looks. System follows your device setting.
              </ToggleDescription>
            </ToggleCopy>
            <AppearancePicker
              value={appearance}
              onChange={handleAppearanceChange}
            />
          </FormGroup>

          <ButtonContainer>
            <ModalCtaButton type='button' primary onClick={handleSave}>Save</ModalCtaButton>
            <ModalCtaButton type='button' onClick={closeModal}>Close</ModalCtaButton>
          </ButtonContainer>
        </ContentSection>
      )}

      {activeTab === 'Keyboard Shortcuts' && (
        <ShortcutSection>
          <ShortcutCategory>
            <CategoryTitle>Calendar</CategoryTitle>
            <ShortcutRow>
              <ShortcutAction>{`Previous ${navUnits}`}</ShortcutAction>
              <ShortcutKey>←</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>{`Next ${navUnits}`}</ShortcutAction>
              <ShortcutKey>→</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Copy Selected Items</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+C</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Paste Items</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+V</ShortcutKey>
            </ShortcutRow>
            {/* Cmd/Ctrl+Z only acts while buildUndo is on (Calendar.js). */}
            {buildUndo && (
              <ShortcutRow>
                <ShortcutAction>Undo</ShortcutAction>
                <ShortcutKey>⌘/Ctrl+Z</ShortcutKey>
              </ShortcutRow>
            )}
          </ShortcutCategory>
          <ShortcutCategory>
            <CategoryTitle>Workout</CategoryTitle>
            <ShortcutRow>
              <ShortcutAction>Add Exercise (Mac)</ShortcutAction>
              <ShortcutKey>Ctrl+N</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Add Exercise (Windows/Linux)</ShortcutAction>
              <ShortcutKey>Alt+N</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Create Exercise while Adding</ShortcutAction>
              <ShortcutKey>Shift+Enter</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Add Property</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+P</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Delete Exercise</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+D</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Add/Edit Additional Info</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+I</ShortcutKey>
            </ShortcutRow>
            <ShortcutRow>
              <ShortcutAction>Additional Options Menu</ShortcutAction>
              <ShortcutKey>⌘/Ctrl+O</ShortcutKey>
            </ShortcutRow>
          </ShortcutCategory>
        </ShortcutSection>
      )}
    </ModalContent>
  );
};

SettingsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default SettingsModal;
